import axios from "@axios";

/**
 * Get language settings from application config
 * This replaces the old systemConfig.language approach
 * @returns {Promise<Array>} Array of language objects with enabled status
 */
export async function getApplicationLanguages() {
  try {
    // Get application context from localStorage
    const applicationId = localStorage.getItem("applicationId");
    const companyId = localStorage.getItem("companyId");
    const franchiseId = localStorage.getItem("franchiseId");

    if (!applicationId || !companyId || !franchiseId) {
      // Return default languages if no application context
      return [
        { name: "Thai", lang: "th", enabled: true },
        { name: "English", lang: "en", enabled: true },
        { name: "Laos", lang: "la", enabled: true },
        { name: "Arabic", lang: "ar", enabled: true },
        { name: "Korean", lang: "ko", enabled: true },
      ];
    }

    const response = await axios.get("/api/system/getApplicationLanguages", {
      params: { applicationId, companyId, franchiseId },
    });

    if (response.data && response.data.data && response.data.data.language) {
      // Return application config languages with enabled status
      return response.data.data.language.map((lang) => ({
        ...lang,
        enabled: lang.enabled !== false, // Default to true if not specified
      }));
    } else {
      // Return default languages if none configured
      return [
        { name: "Thai", lang: "th", enabled: true },
        { name: "English", lang: "en", enabled: true },
        { name: "Laos", lang: "la", enabled: true },
        { name: "Arabic", lang: "ar", enabled: true },
        { name: "Korean", lang: "ko", enabled: true },
      ];
    }
  } catch (error) {
    console.error("Error fetching application languages:", error);
    // Return default languages on error
    return [
      { name: "Thai", lang: "th", enabled: true },
      { name: "English", lang: "en", enabled: true },
      { name: "Laos", lang: "la", enabled: true },
      { name: "Arabic", lang: "ar", enabled: true },
      { name: "Korean", lang: "ko", enabled: true },
    ];
  }
}

/**
 * Get only enabled languages from application config
 * @returns {Promise<Array>} Array of enabled language objects
 */
export async function getEnabledLanguages() {
  const allLanguages = await getApplicationLanguages();
  return allLanguages.filter((lang) => lang.enabled === true);
}

/**
 * Get language codes (short form) from application config
 * @returns {Promise<Array>} Array of language codes
 */
export async function getLanguageCodes() {
  const enabledLanguages = await getEnabledLanguages();
  return enabledLanguages.map((lang) => lang.lang);
}

/**
 * Get language names from application config
 * @returns {Promise<Array>} Array of language names
 */
export async function getLanguageNames() {
  const enabledLanguages = await getEnabledLanguages();
  return enabledLanguages.map((lang) => lang.name);
}

/**
 * Check if a specific language is enabled
 * @param {string} langCode - Language code to check (e.g., 'th', 'en')
 * @returns {Promise<boolean>} True if language is enabled
 */
export async function isLanguageEnabled(langCode) {
  const allLanguages = await getApplicationLanguages();
  const language = allLanguages.find((lang) => lang.lang === langCode);
  return language ? language.enabled === true : false;
}

/**
 * Get language object by language code
 * @param {string} langCode - Language code to find (e.g., 'th', 'en')
 * @returns {Promise<Object|null>} Language object or null if not found
 */
export async function getLanguageByCode(langCode) {
  const allLanguages = await getApplicationLanguages();
  return allLanguages.find((lang) => lang.lang === langCode) || null;
}
